/**
 * 키오스크 모드 전용 타입 정의
 */

/** 키오스크 인디케이터 상태 */
export type KioskIndicatorState = 'idle' | 'listening' | 'loading' | 'speaking';

/** 키오스크 커버 모달 Props */
export interface KioskCoverModalProps {
  isOpen: boolean;
  isLoading: boolean;
  loadingMessage?: string;
  /** 커버 동영상 URL */
  videoUrl?: string;
  /** 메인 타이틀 (줄바꿈 포함) */
  title?: string;
  /** CTA 버튼 텍스트 */
  buttonText?: string;
  /** 버튼 클릭 핸들러 */
  onStart: () => void;
}

/** 키오스크 인디케이터 Props */
export interface KioskIndicatorProps {
  state: KioskIndicatorState;
  /**
   * @deprecated bubbleText는 KioskToastContainer에서 토스트로 처리됨
   * KioskIndicator에서는 Omit<KioskIndicatorProps, 'bubbleText'>로 사용
   */
  bubbleText?: string;
  /** 상태 버튼 텍스트 (기본값 있음) */
  statusText?: string;
  /** Speaking 상태에서 화면 터치 시 멈춤 */
  onScreenTouch?: () => void;
  /** Listening 상태에서 이퀄라이저 애니메이션을 위한 pitch 값 (0-100) */
  pitch?: number;
  /** 선택된 언어 */
  selectedLanguage: SupportedKioskLanguage;
}

/** 언어 선택기 Props */
export interface KioskLanguageSelectorProps {
  selectedLanguage: SupportedKioskLanguage;
  onLanguageChange: (lang: SupportedKioskLanguage) => void;
  isOpen?: boolean;
  onToggle?: () => void;
}

/** 지원 언어 */
export type SupportedKioskLanguage = 'ko' | 'en' | 'zh' | 'ja';

/** 언어 정보 */
export interface LanguageInfo {
  code: SupportedKioskLanguage;
  /** @deprecated 이모지 fallback용 - flagImage 사용 권장 */
  flag: string;
  /** 국기 이미지 URL (flagcdn.com 사용) */
  flagImage: string;
  name: string;
}

/** 지원 언어 목록 */
export const KIOSK_LANGUAGES: LanguageInfo[] = [
  { code: 'ko', flag: '🇰🇷', flagImage: '/svg/flag_kr.svg', name: '한국어' },
  { code: 'en', flag: '🇺🇸', flagImage: '/svg/flag_en.svg', name: 'English' },
  { code: 'zh', flag: '🇨🇳', flagImage: '/svg/flag_zh.svg', name: '中文' },
  { code: 'ja', flag: '🇯🇵', flagImage: '/svg/flag_ja.svg', name: '日本語' },
];

/** 상태별 다국어 텍스트 */
export const INDICATOR_STATUS_TEXT: Record<SupportedKioskLanguage, Record<KioskIndicatorState, string>> = {
  ko: {
    idle: '',
    listening: '듣고 있어요',
    loading: '생각 중이에요',
    speaking: '터치하면 바로 말할 수 있어요',
  },
  en: {
    idle: '',
    listening: 'Listening',
    loading: 'Thinking',
    speaking: 'Tap to stop',
  },
  zh: {
    idle: '',
    listening: '正在聆听',
    loading: '正在思考',
    speaking: '点击暂停',
  },
  ja: {
    idle: '',
    listening: '聞いています',
    loading: '考え中',
    speaking: 'タップで停止',
  },
};

/** 유휴 타이머 옵션 */
export interface IdleTimerOptions {
  /** 전체 타임아웃 (밀리초) */
  timeoutMs: number;
  /** 경고 표시 시작 시점 (남은 밀리초) */
  warningMs: number;
  /** 타임아웃 시 콜백 */
  onTimeout: () => void;
  /** 경고 업데이트 콜백 (남은 밀리초) */
  onWarningUpdate?: (remainingMs: number) => void;
}

/** 통합 로딩 상태 */
export type KioskLoadingPhase =
  | 'resolving-slug'    // 슬러그 → agentId 변환 중
  | 'loading-agent'     // 에이전트 데이터 로딩 중
  | 'loading-avatar'    // 커스텀 아바타 데이터 로딩 중
  | 'loading-3d'        // 3D 아바타 렌더링 로딩 중
  | 'ready';            // 모든 로딩 완료

/** 로딩 단계별 메시지 */
export const KIOSK_LOADING_MESSAGES: Record<KioskLoadingPhase, string> = {
  'resolving-slug': '페이지 로딩 중...',
  'loading-agent': '정보를 불러오는 중...',
  'loading-avatar': '아바타 데이터 준비 중...',
  'loading-3d': '아바타를 불러오는 중...',
  'ready': '',
};

/** 키오스크 페이지 Props */
export interface KioskAvatarPageProps {
  agentId: string;
}

/** 헤더 타이틀 다국어 텍스트 */
export const HEADER_TITLE_TEXT: Record<SupportedKioskLanguage, string> = {
  ko: 'AI 민원 도우미',
  en: 'AI Civil Service',
  zh: 'AI 民事助手',
  ja: 'AI 窓口案内',
};

/** 키오스크 헤더 Props */
export interface KioskHeaderProps {
  /** 로고 클릭 시 전체 세션 리셋 */
  onLogoClick?: () => void;
  /** 헤더 타이틀 (예: "AI 민원 도우미") */
  title?: string;
  /** 선택된 언어 */
  selectedLanguage?: SupportedKioskLanguage;
}

/** 언어 버튼 Props (헤더 내부용) */
export interface KioskLanguageButtonProps {
  selectedLanguage: SupportedKioskLanguage;
  onClick: () => void;
}

/** 언어 드롭다운 Props (열린 상태) */
export interface KioskLanguageDropdownProps {
  selectedLanguage: SupportedKioskLanguage;
  onSelect: (lang: SupportedKioskLanguage) => void;
  onClose: () => void;
}

/** 취소 버튼 다국어 텍스트 */
export const CANCEL_TEXT: Record<SupportedKioskLanguage, string> = {
  ko: '취소',
  en: 'Cancel',
  zh: '取消',
  ja: 'キャンセル',
};

/** 대화 기록 버튼 텍스트 */
export const CHAT_HISTORY_BUTTON_TEXT: Record<SupportedKioskLanguage, string> = {
  ko: '대화 기록',
  en: 'Chat History',
  zh: '对话记录',
  ja: '会話履歴',
};

/** 닫기 버튼 텍스트 */
export const CLOSE_BUTTON_TEXT: Record<SupportedKioskLanguage, string> = {
  ko: '닫기',
  en: 'Close',
  zh: '关闭',
  ja: '閉じる',
};

/** 대화 종료 버튼 텍스트 */
export const END_CHAT_BUTTON_TEXT: Record<SupportedKioskLanguage, string> = {
  ko: '대화 종료',
  en: 'End Chat',
  zh: '结束对话',
  ja: '会話終了',
};

/** AI 면책 안내 문구 */
export const AI_DISCLAIMER_TEXT: Record<SupportedKioskLanguage, string> = {
  ko: 'AI 아바타는 실수할 수 있어요. 응답을 다시 한번 확인해주세요.',
  en: 'AI avatars can make mistakes. Please double-check the responses.',
  zh: 'AI虚拟人可能会出错，请再次确认回复内容。',
  ja: 'AIアバターは間違えることがあります。回答を再度ご確認ください。',
};
